import { DeleteOutlined } from "@ant-design/icons";
import { Button, Checkbox, Space, Tooltip } from "antd";
import React, { useState } from "react";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal/DeleteConfirmationModal";

export type PlanDeletionOptions = {
  deleteComponents: boolean;
  deleteMappings: boolean;
};

type PlanDeletionModalProps = {
  onDelete: (options: PlanDeletionOptions) => Promise<void>;
};

const DELETE_CONFIRMATION_REQ_TEXT = "delete plan";
const DELETE_PLAN_MODAL_TITLE = "Delete Plan";
const DELETE_CONFIRMATION_ALERT_MESSAGE =
  "Are you sure you want to delete the plan?";
const TOOLTIP_TITLE = "delete plan";

export const PlanDeletionModal = (props: PlanDeletionModalProps) => {
  const { onDelete } = props;

  const [visible, setVisible] = useState(false);
  const [deleteComponents, setDeleteComponents] = useState(false);
  const [deleteMappings, setDeleteMappings] = useState(false);

  const resetOptions = () => {
    setDeleteComponents(false);
    setDeleteMappings(false);
  };

  const handleSetVisible = (nextVisible: boolean) => {
    if (!nextVisible) {
      resetOptions();
    }
    setVisible(nextVisible);
  };

  const modalBody = (
    <>
      <p>
        This action will delete the plan and its tasks. By default their
        components are unassigned and retained.
      </p>
      <Space direction="vertical">
        <Checkbox
          checked={deleteComponents}
          onChange={(e) => {
            setDeleteComponents(e.target.checked);
            // Mutually exclusive: deleting components inherently removes their
            // mappings, so the mappings-only option cannot also be selected.
            if (e.target.checked) {
              setDeleteMappings(false);
            }
          }}
        >
          Delete components too (also deletes their mappings)
        </Checkbox>
        <Checkbox
          checked={deleteMappings}
          onChange={(e) => {
            setDeleteMappings(e.target.checked);
            if (e.target.checked) {
              setDeleteComponents(false);
            }
          }}
        >
          Delete mappings only (keeps components in the unassigned list)
        </Checkbox>
      </Space>
    </>
  );

  return (
    <>
      <Tooltip title={TOOLTIP_TITLE}>
        <Button
          danger
          data-testid="delete-plan-button"
          type="primary"
          onClick={() => setVisible(true)}
          icon={<DeleteOutlined />}
        />
      </Tooltip>
      <DeleteConfirmationModal
        title={DELETE_PLAN_MODAL_TITLE}
        visible={visible}
        setVisible={handleSetVisible}
        onOk={() => onDelete({ deleteComponents, deleteMappings })}
        deletionText={DELETE_CONFIRMATION_REQ_TEXT}
        modalBody={modalBody}
        alertText={DELETE_CONFIRMATION_ALERT_MESSAGE}
      />
    </>
  );
};
